import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './StatReportsPage.module.css';
import commonStyles from '../../common/CommonStyles.module.css';
import { useNavigate } from 'react-router-dom';

const StatReportsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get('/api/finance/stat-reports');
      setReports(response.data);
      setError(null);
    } catch (error) {
      console.error('Ошибка при загрузке отчетов:', error);
      setError('Не удалось загрузить отчеты');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Нет данных';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className={commonStyles.pageContainer}>
        <div className={commonStyles.contentContainer}>
          <div className={commonStyles.content}>
            <div className={styles.loading}>Загрузка данных...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={commonStyles.pageContainer}>
      <div className={commonStyles.contentContainer}>
        <div className={commonStyles.content}>
          <div className={styles.headerRow}>
            <h2 className={styles.title}>Статистические отчеты</h2>
            <button
              className={styles.summaryButton}
              onClick={() => navigate('/admin/stat-reports/summary')}
            >
              Сводные таблицы
            </button>
          </div>

          {error && (
            <div className={styles.error}>
              {error}
              <button onClick={fetchReports} className={styles.retryButton}>
                Попробовать снова
              </button>
            </div>
          )}

          {!selectedReport ? (
            <>
              <p className={styles.count}>Всего отчетов: {reports.length}</p>

              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Федеральный округ</th>
                      <th>Регион</th>
                      <th>Дата создания</th>
                      <th>Действие</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.length === 0 ? (
                      <tr>
                        <td colSpan="4" className={styles.noData}>
                          Нет данных
                        </td>
                      </tr>
                    ) : (
                      reports.map(report => (
                        <tr key={report.id}>
                          <td>{report.federalDistrict?.name || 'Нет данных'}</td>
                          <td>{report.region?.name || 'Нет данных'}</td>
                          <td>{formatDate(report.createdAt)}</td>
                          <td>
                            <button
                              className={styles.viewButton}
                              onClick={() => setSelectedReport(report)}
                            >
                              Просмотр
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className={styles.detailView}>
              <button className={styles.backButton} onClick={() => setSelectedReport(null)}>
                ← Вернуться к списку
              </button>

              <h3 className={styles.detailTitle}>Детали отчета</h3>

              <div className={styles.detailSection}>
                <h4 className={styles.detailSectionTitle}>Основная информация</h4>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Федеральный округ:</span>
                    <span className={styles.detailValue}>{selectedReport.federalDistrict?.name || 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Регион:</span>
                    <span className={styles.detailValue}>{selectedReport.region?.name || 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Количество первичных отделений:</span>
                    <span className={styles.detailValue}>{selectedReport.primaryDivisionsCount ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Количество местных отделений:</span>
                    <span className={styles.detailValue}>{selectedReport.localDivisionsCount ?? 'Нет данных'}</span>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h4 className={styles.detailSectionTitle}>Члены ЛКСМ РФ</h4>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Всего:</span>
                    <span className={styles.detailValue}>{selectedReport.membersTotal ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>До 18 лет:</span>
                    <span className={styles.detailValue}>{selectedReport.membersUnder18 ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Старше 35 лет:</span>
                    <span className={styles.detailValue}>{selectedReport.membersOver35 ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Средний возраст:</span>
                    <span className={styles.detailValue}>{selectedReport.averageAge ?? 'Нет данных'}</span>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h4 className={styles.detailSectionTitle}>По социальному статусу</h4>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Служащие:</span>
                    <span className={styles.detailValue}>{selectedReport.membersEmployees ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Рабочие:</span>
                    <span className={styles.detailValue}>{selectedReport.membersWorkers ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Студенты:</span>
                    <span className={styles.detailValue}>{selectedReport.membersStudents ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Школьники:</span>
                    <span className={styles.detailValue}>{selectedReport.membersSchoolchildren ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Безработные:</span>
                    <span className={styles.detailValue}>{selectedReport.membersUnemployed ?? 'Нет данных'}</span>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h4 className={styles.detailSectionTitle}>Взносы и движение</h4>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Уплачивающих членские взносы:</span>
                    <span className={styles.detailValue}>{selectedReport.membersPayingDues ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Выбывших за период (всего):</span>
                    <span className={styles.detailValue}>{selectedReport.membersDepartedTotal ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Исключённых из ЛКСМ РФ:</span>
                    <span className={styles.detailValue}>{selectedReport.membersExcluded ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Вступивших за период:</span>
                    <span className={styles.detailValue}>{selectedReport.membersJoined ?? 'Нет данных'}</span>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h4 className={styles.detailSectionTitle}>Комитет регионального отделения</h4>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Членов Комитета:</span>
                    <span className={styles.detailValue}>{selectedReport.committeeMembersCount ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Членов Бюро Комитета:</span>
                    <span className={styles.detailValue}>{selectedReport.bureauMembersCount ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Секретарей Комитета:</span>
                    <span className={styles.detailValue}>{selectedReport.secretariesCount ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Штатных работников Комитета:</span>
                    <span className={styles.detailValue}>{selectedReport.staffWorkersCount ?? 'Нет данных'}</span>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h4 className={styles.detailSectionTitle}>Депутаты и помощники</h4>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Депутаты (законодательный орган субъекта РФ):</span>
                    <span className={styles.detailValue}>{selectedReport.deputiesRegionalLegislative ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Депутаты (представительные органы МСУ):</span>
                    <span className={styles.detailValue}>{selectedReport.deputiesLocalRepresentative ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Помощники (штатная основа):</span>
                    <span className={styles.detailValue}>{selectedReport.assistantsPermanent ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Помощники (общественные начала):</span>
                    <span className={styles.detailValue}>{selectedReport.assistantsVoluntary ?? 'Нет данных'}</span>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h4 className={styles.detailSectionTitle}>Молодёжные консультативные органы</h4>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>При законодательном органе субъекта РФ:</span>
                    <span className={styles.detailValue}>{selectedReport.youthCouncilRegional ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>При представительных органах МСУ:</span>
                    <span className={styles.detailValue}>{selectedReport.youthCouncilLocal ?? 'Нет данных'}</span>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h4 className={styles.detailSectionTitle}>Другие организации</h4>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Членов пионерской организации:</span>
                    <span className={styles.detailValue}>{selectedReport.pioneerMembersCount ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Членов НСП «Дискурс»:</span>
                    <span className={styles.detailValue}>{selectedReport.discourseMembersCount ?? 'Нет данных'}</span>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h4 className={styles.detailSectionTitle}>Комсомольская учёба</h4>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Кружков/школ/семинаров/лекториев (всего):</span>
                    <span className={styles.detailValue}>{selectedReport.studyCirclesTotal ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Посещающих занятия:</span>
                    <span className={styles.detailValue}>{selectedReport.studyCirclesAttendees ?? 'Нет данных'}</span>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h4 className={styles.detailSectionTitle}>Контактная информация</h4>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Адрес регионального комитета:</span>
                    <span className={styles.detailValue}>{selectedReport.committeeAddress || 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Почта:</span>
                    <span className={styles.detailValue}>{selectedReport.committeeEmail || 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Ссылка на группу в ВКонтакте:</span>
                    <span className={styles.detailValue}>
                      {selectedReport.vkGroupLink ? (
                        <a href={selectedReport.vkGroupLink} target="_blank" rel="noopener noreferrer">
                          {selectedReport.vkGroupLink}
                        </a>
                      ) : 'Нет данных'}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Ссылка на канал в Телеграмм:</span>
                    <span className={styles.detailValue}>
                      {selectedReport.telegramChannelLink ? (
                        <a href={selectedReport.telegramChannelLink} target="_blank" rel="noopener noreferrer">
                          {selectedReport.telegramChannelLink}
                        </a>
                      ) : 'Нет данных'}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>ФИО первого секретаря:</span>
                    <span className={styles.detailValue}>{selectedReport.firstSecretaryName || 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Другие соцсети и сайт (ссылки):</span>
                    <span className={styles.detailValue}>{selectedReport.otherSocialLinks || 'Нет данных'}</span>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h4 className={styles.detailSectionTitle}>Социальные сети</h4>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Наличие сайта:</span>
                    <span className={styles.detailValue}>{selectedReport.hasWebsite ? 'Да' : 'Нет'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Количество подписчиков:</span>
                    <span className={styles.detailValue}>{selectedReport.socialNetworksCount ?? 'Нет данных'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Телеграм:</span>
                    <span className={styles.detailValue}>{selectedReport.socialTelegram ? 'Да' : 'Нет'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>ВКонтакте:</span>
                    <span className={styles.detailValue}>{selectedReport.socialVk ? 'Да' : 'Нет'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Одноклассники:</span>
                    <span className={styles.detailValue}>{selectedReport.socialOk ? 'Да' : 'Нет'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>TikTok:</span>
                    <span className={styles.detailValue}>{selectedReport.socialTiktok ? 'Да' : 'Нет'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Макс:</span>
                    <span className={styles.detailValue}>{selectedReport.socialMax ? 'Да' : 'Нет'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Иное:</span>
                    <span className={styles.detailValue}>{selectedReport.socialOther ? 'Да' : 'Нет'}</span>
                  </div>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h4 className={styles.detailSectionTitle}>Дата создания</h4>
                <div className={styles.detailItem}>
                  <span className={styles.detailValue}>{formatDate(selectedReport.createdAt)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatReportsPage;
